import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { FaHeart } from "react-icons/fa";
import { FaMoneyBill1Wave } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import { getUserData, purchaseHearts } from "../../utils/userStorage.ts";

export const QuestionProgressBar = ({ progress = 0 }) => (
  <div className="w-full bg-gray-300 rounded-full h-2">
    <div
      className="bg-lime-500 h-2 rounded-full transition-all duration-300"
      style={{ width: `${Math.round(progress * 100)}%` }}
    ></div>
  </div>
);

export const QuestionHeader = ({ hearts, progress, darkMode = false }) => {
  const navigate = useNavigate();
  const [showConfirm, setShowConfirm] = React.useState(false);
  const [showGameOverModal, setShowGameOverModal] = React.useState(false);
  const [userData, setUserData] = useState(() => getUserData());

  useEffect(() => {
    const handleUserDataChange = () => {
      const newUserData = getUserData();
      setUserData(newUserData);
    };
    if (hearts === 0) {
      setShowGameOverModal(true);
    } else {
      setShowGameOverModal(false);
    }
  }, [hearts]);

  const handleBack = () => {
    setShowConfirm(true);
  };

  const handleExitLesson = () => {
    setShowConfirm(false);
    navigate(-1);
  };

  const handleCancel = () => {
    setShowConfirm(false);
  };

  const handleBuyHeart = () => {
    setShowGameOverModal(false);
    const result = purchaseHearts(1);
    if (result.success) {
      window.dispatchEvent(new Event("userDataChanged"));
    } else {
      alert(result.message);
    }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        {/* X Button */}
        <button onClick={handleBack} className="flex-shrink-0">
          <X
            className={`w-6 h-6 ${
              darkMode ? "text-gray-300" : "text-gray-600"
            }`}
          />
        </button>
        {/* Progress Bar */}
        <div className="flex-1 mx-4">
          <QuestionProgressBar progress={progress} />
        </div>
        {/* Heart */}
        <div className="flex items-center flex-shrink-0">
          <FaHeart className="w-5 h-5 text-red-500 mr-1" />
          <span className="text-red-500 font-bold">{hearts}</span>
        </div>
      </div>
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
          <div
            className={`rounded-xl shadow-lg py-8 px-4 min-w-[300px] text-center border transition-colors duration-200 ${
              darkMode
                ? "bg-gray-800 border-2 border-gray-700"
                : "bg-white border-gray-200"
            }`}
          >
            <div
              className={`text-xl font-bold ${
                darkMode ? "text-gray-100" : "text-gray-800"
              }`}
              style={{ fontFamily: "Feather Bold" }}
            >
              Apakah kamu yakin ingin keluar?
            </div>
            <div
              className={`text-md font-base mb-4 ${
                darkMode ? "text-gray-300" : "text-gray-800"
              }`}
              style={{ fontFamily: "DIN Next Rounded" }}
            >
              Semua progres pada sesi ini akan hilang.
            </div>
            <div className="flex justify-center gap-4 mt-2">
              <button
                onClick={handleExitLesson}
                className="md:px-6 px-4 md:py-2 py-2 rounded-lg bg-red-500 text-white font-bold hover:bg-red-600 transition md:text-base text-sm"
                style={{ fontFamily: "DIN Next Rounded" }}
              >
                Keluar
              </button>
              <button
                onClick={handleCancel}
                className={`md:px-6 px-4 md:py-2 py-2 rounded-lg font-bold transition md:text-base text-sm ${
                  darkMode
                    ? "bg-gray-700 text-gray-200 hover:bg-gray-600"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
                style={{ fontFamily: "DIN Next Rounded" }}
              >
                Batal
              </button>
            </div>
          </div>
        </div>
      )}
      {showGameOverModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
          <div
            className={`rounded-xl shadow-lg py-7 px-3 min-w-[280px] max-w-xs sm:max-w-md text-center border transition-colors duration-200 relative ${
              darkMode
                ? "bg-gray-800 border-2 border-gray-700"
                : "bg-white border-gray-200"
            }`}
          >
            {/* User money in top right corner */}
            <div className="absolute top-3 right-4 flex items-center gap-1">
              <span className="text-green-500 font-bold">
                <FaMoneyBill1Wave className="mr-1 size-5" />
              </span>
              <span
                className="text-green-500 font-bold"
                style={{ fontFamily: "DIN Next Rounded" }}
              >
                {userData?.meta?.money?.toLocaleString("id-ID") ?? 0}
              </span>
            </div>
            <div
              className={`text-xl font-bold mb-2 mt-3 ${
                darkMode ? "text-gray-100" : "text-gray-800"
              }`}
              style={{ fontFamily: "Feather Bold" }}
            >
              Hati kamu habis!
            </div>
            <div
              className={`text-md font-base mb-5 ${
                darkMode ? "text-gray-300" : "text-gray-800"
              }`}
              style={{ fontFamily: "DIN Next Rounded" }}
            >
              Silakan isi ulang hati untuk melanjutkan belajar, atau keluar ke
              halaman utama.
            </div>
            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 mt-2">
              <button
                onClick={handleExitLesson}
                className={`flex-1 flex items-center justify-center md:px-6 px-4 md:py-2 py-2 rounded-lg border transition text-xs font-semibold ${
                  darkMode
                    ? "bg-red-700 border-red-600 text-white hover:bg-red-800"
                    : "bg-red-400 border-red-500 text-white hover:bg-red-600"
                }`}
                style={{ fontFamily: "DIN Next Rounded" }}
              >
                Keluar
              </button>
              <button
                onClick={handleBuyHeart}
                className={`flex-1 flex items-center justify-center md:px-6 px-4 md:py-2 py-2 rounded-lg border transition text-xs font-semibold ${
                  (userData?.meta?.money ?? 0) < 50
                    ? "opacity-50 cursor-not-allowed"
                    : darkMode
                    ? "bg-green-700 border-green-600 text-white hover:bg-green-800"
                    : "bg-green-500 border-green-400 text-white hover:bg-green-600"
                }`}
                style={{ fontFamily: "DIN Next Rounded" }}
                disabled={(userData?.meta?.money || 0) < 50}
              >
                <span className="flex items-center">
                  <FaHeart className="mr-1 text-red-500" /> Isi Ulang Hati
                </span>
                <span className="flex items-center text-green-200 ml-2">
                  <FaMoneyBill1Wave className="mr-1" />
                  50
                </span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
